import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Contact from '../../entities/contact';
import Matter from '../../entities/matter';
import Conflict from '../../entities/conflict';
import Expense from '../../entities/expense';
import Event from '../../entities/event';
import FlatFee from '../../entities/flatFee';
import Invoice from '../../entities/invoice';
import Lead from '../../entities/lead';
import Participant from '../../entities/participant';
import Referral from '../../entities/referral';
import Task from '../../entities/task';
import Transaction from '../../entities/transaction';
import User from '../../entities/user';
import Address from '../../entities/address';
import Answer from '../../entities/answer';
import Comment from '../../entities/comment';
import Document from '../../entities/document';
import Identification from '../../entities/identification';
import Resource from '../../entities/resource';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteAddress, collectAddressDeleteImpact} from './addressDeps';
import {deleteAnswer, collectAnswerDeleteImpact} from './answerDeps';
import {deleteComment, collectCommentDeleteImpact} from './commentDeps';
import {deleteDocument, collectDocumentDeleteImpact} from './documentDeps';
import {deleteEvent, collectEventDeleteImpact} from './eventDeps';
import {deleteIdentification, collectIdentificationDeleteImpact} from './identificationDeps';
import {deleteParticipant, collectParticipantDeleteImpact} from './participantDeps';
import {deleteResource, collectResourceDeleteImpact} from './resourceDeps';
export function canDeleteContact(): boolean {
return canCascadeDelete();
}
export function collectContactDeleteImpact(contact: Contact, context: DeleteImpactContext): void {
const key = 'contact:' + contact.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const matterClientsImpact = workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matterRef => matterRef.clients?.includes(contact.id));
matterClientsImpact.forEach(matterRef => addDeleteImpact(context, 'matter:' + matterRef.id, 'matters*'));
const matterBillToContactIdImpact = workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matterRef => matterRef.billToContactId === contact.id);
matterBillToContactIdImpact.forEach(matterRef => addDeleteImpact(context, 'matter:' + matterRef.id, 'matters'));
const conflictCheckedContactIdsImpact = workingRows<Conflict>('conflict', findAll<Conflict>('conflicts')).filter(conflictRef => conflictRef.checkedContactIds?.includes(contact.id));
conflictCheckedContactIdsImpact.forEach(conflictRef => addDeleteImpact(context, 'conflict:' + conflictRef.id, 'conflicts'));
const contactCompanyIdImpact = workingRows<Contact>('contact', findAll<Contact>('contacts')).filter(contactRef => contactRef.companyId === contact.id);
contactCompanyIdImpact.forEach(contactRef => addDeleteImpact(context, 'contact:' + contactRef.id, 'contacts'));
const expensePayeeImpact = workingRows<Expense>('expense', findAll<Expense>('expenses')).filter(expenseRef => expenseRef.payee === contact.id);
expensePayeeImpact.forEach(expenseRef => addDeleteImpact(context, 'expense:' + expenseRef.id, 'expenses'));
const eventParticipantsImpact = workingRows<Event>('event', findAll<Event>('events')).filter(eventRef => eventRef.participants?.includes(contact.id));
eventParticipantsImpact.forEach(eventRef => addDeleteImpact(context, 'event:' + eventRef.id, 'events'));
const flatFeeRelatedToImpact = workingRows<FlatFee>('flatFee', findAll<FlatFee>('flatFees')).filter(flatFeeRef => flatFeeRef.relatedTo === contact.id);
flatFeeRelatedToImpact.forEach(flatFeeRef => addDeleteImpact(context, 'flatFee:' + flatFeeRef.id, 'flatFees'));
const invoiceClientIdsImpact = workingRows<Invoice>('invoice', findAll<Invoice>('invoices')).filter(invoiceRef => invoiceRef.clientIds?.includes(contact.id) && !(invoiceRef.invoiceStatus === 'F'));
invoiceClientIdsImpact.forEach(invoiceRef => addDeleteImpact(context, 'invoice:' + invoiceRef.id, 'invoices'));
const leadClientsImpact = workingRows<Lead>('lead', findAll<Lead>('leads')).filter(leadRef => leadRef.clients?.includes(contact.id));
leadClientsImpact.forEach(leadRef => addDeleteImpact(context, 'lead:' + leadRef.id, 'leads*'));
const leadPayersImpact = workingRows<Lead>('lead', findAll<Lead>('leads')).filter(leadRef => leadRef.payers?.includes(contact.id));
leadPayersImpact.forEach(leadRef => addDeleteImpact(context, 'lead:' + leadRef.id, 'leads'));
const leadReferrersImpact = workingRows<Lead>('lead', findAll<Lead>('leads')).filter(leadRef => leadRef.referrers?.includes(contact.id));
leadReferrersImpact.forEach(leadRef => addDeleteImpact(context, 'lead:' + leadRef.id, 'leads'));
const participantContactIdImpact = workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participantRef => participantRef.contactId === contact.id);
participantContactIdImpact.forEach(participantRef => addDeleteImpact(context, 'participant:' + participantRef.id, 'participants*'));
const referralRefereeImpact = workingRows<Referral>('referral', findAll<Referral>('referrals')).filter(referralRef => referralRef.referee === contact.id);
referralRefereeImpact.forEach(referralRef => addDeleteImpact(context, 'referral:' + referralRef.id, 'referrals'));
const taskRelatedToImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.relatedTo?.includes(contact.id));
taskRelatedToImpact.forEach(taskRef => addDeleteImpact(context, 'task:' + taskRef.id, 'tasks'));
const transactionClientIdsImpact = workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionRef => transactionRef.clientIds?.includes(contact.id) && !(findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(transactionRef.id) || invoice.id === transactionRef.invoiceId)).length > 0));
transactionClientIdsImpact.forEach(transactionRef => addDeleteImpact(context, 'transaction:' + transactionRef.id, 'transactions'));
const userContactIdImpact = workingRows<User>('user', findAll<User>('users')).filter(userRef => userRef.contactId === contact.id);
userContactIdImpact.forEach(userRef => addDeleteImpact(context, 'user:' + userRef.id, 'users'));const transactionSourceTypeImpact = workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionRef => ['C','G'].includes(transactionRef.sourceType || '') && transactionRef.source === contact.id && transactionRef.status !== 'D');
transactionSourceTypeImpact.forEach(transaction => addDeleteImpact(context, 'transaction:' + transaction.id, 'transactions'));
const transactionTargetTypeImpact = workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionRef => ['C','G'].includes(transactionRef.targetType || '') && transactionRef.target === contact.id && transactionRef.status !== 'D');
transactionTargetTypeImpact.forEach(transaction => addDeleteImpact(context, 'transaction:' + transaction.id, 'transactions'));workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matter => matter.status !== 'D' && matter.clients?.includes(contact.id)).forEach(matter => {
addDeleteImpact(context, 'matter:' + matter.id, 'matters*');
context.visited.add('matter:' + matter.id);
});
workingRows<Lead>('lead', findAll<Lead>('leads')).filter(lead => lead.status !== 'D' && lead.clients?.includes(contact.id)).forEach(lead => {
addDeleteImpact(context, 'lead:' + lead.id, 'leads*');
context.visited.add('lead:' + lead.id);
});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.contactId === contact.id).forEach(participant => {
addDeleteImpact(context, 'participant:' + participant.id, 'participants*');
context.visited.add('participant:' + participant.id);
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transaction => transaction.status !== 'D' && transaction.clientIds?.includes(contact.id)).forEach(transaction => {
addDeleteImpact(context, 'transaction:' + transaction.id, 'transactions*');
context.visited.add('transaction:' + transaction.id);
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transaction => transaction.status !== 'D' && ['C', 'G'].includes(transaction.sourceType || '') && transaction.source === contact.id).forEach(transaction => {
addDeleteImpact(context, 'transaction:' + transaction.id, 'transactions*');
context.visited.add('transaction:' + transaction.id);
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transaction => transaction.status !== 'D' && ['C', 'G'].includes(transaction.targetType || '') && transaction.target === contact.id).forEach(transaction => {
addDeleteImpact(context, 'transaction:' + transaction.id, 'transactions*');
context.visited.add('transaction:' + transaction.id);
});
workingRows<Address>('address', findAll<Address>('addresses')).filter(address => address.status !== 'D' && address.entityType === 'contact' && address.entityId === contact.id).forEach(address => {
if (context.visited.has('address:' + address.id)) return;
addDeleteImpact(context, 'address:' + address.id, 'addresses');
collectAddressDeleteImpact(address, context);
});
workingRows<Answer>('answer', findAll<Answer>('answers')).filter(answer => answer.status !== 'D' && answer.entityType === 'contact' && answer.entityId === contact.id).forEach(answer => {
if (context.visited.has('answer:' + answer.id)) return;
addDeleteImpact(context, 'answer:' + answer.id, 'answers');
collectAnswerDeleteImpact(answer, context);
});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'contact' && comment.entityId === contact.id).forEach(comment => {
if (context.visited.has('comment:' + comment.id)) return;
addDeleteImpact(context, 'comment:' + comment.id, 'comments');
collectCommentDeleteImpact(comment, context);
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'contact' && document.entityId === contact.id).forEach(document => {
if (context.visited.has('document:' + document.id)) return;
addDeleteImpact(context, 'document:' + document.id, 'documents');
collectDocumentDeleteImpact(document, context);
});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'contact' && event.entityId === contact.id).forEach(event => {
if (context.visited.has('event:' + event.id)) return;
addDeleteImpact(context, 'event:' + event.id, 'events');
collectEventDeleteImpact(event, context);
});
workingRows<Identification>('identification', findAll<Identification>('identifications')).filter(identification => identification.status !== 'D' && identification.entityType === 'contact' && identification.entityId === contact.id).forEach(identification => {
if (context.visited.has('identification:' + identification.id)) return;
addDeleteImpact(context, 'identification:' + identification.id, 'identifications');
collectIdentificationDeleteImpact(identification, context);
});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'contact' && participant.entityId === contact.id).forEach(participant => {
if (context.visited.has('participant:' + participant.id)) return;
addDeleteImpact(context, 'participant:' + participant.id, 'participants');
collectParticipantDeleteImpact(participant, context);
});
workingRows<Resource>('resource', findAll<Resource>('resources')).filter(resource => resource.status !== 'D' && resource.entityType === 'contact' && resource.entityId === contact.id).forEach(resource => {
if (context.visited.has('resource:' + resource.id)) return;
addDeleteImpact(context, 'resource:' + resource.id, 'resources');
collectResourceDeleteImpact(resource, context);
});
}
export function deleteImpactContact(contact: Contact): DeleteImpact {
const context = deleteImpactContext();
collectContactDeleteImpact(contact, context);
return deleteImpactCounts(context);
}
export function deleteContact(contact: Contact, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteContact()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectContactDeleteImpact(contact, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || contact.id};
}
const key = 'contact:' + contact.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Address>('address', findAll<Address>('addresses')).filter(address => address.status !== 'D' && address.entityType === 'contact' && address.entityId === contact.id).forEach(address => {deleteAddress(address, mergeId, cascade);});
workingRows<Answer>('answer', findAll<Answer>('answers')).filter(answer => answer.status !== 'D' && answer.entityType === 'contact' && answer.entityId === contact.id).forEach(answer => {deleteAnswer(answer, mergeId, cascade);});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'contact' && comment.entityId === contact.id).forEach(comment => {deleteComment(comment, mergeId, cascade);});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'contact' && document.entityId === contact.id).forEach(document => {deleteDocument(document, mergeId, cascade);});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'contact' && event.entityId === contact.id).forEach(event => {deleteEvent(event, mergeId, cascade);});
workingRows<Identification>('identification', findAll<Identification>('identifications')).filter(identification => identification.status !== 'D' && identification.entityType === 'contact' && identification.entityId === contact.id).forEach(identification => {deleteIdentification(identification, mergeId, cascade);});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'contact' && participant.entityId === contact.id).forEach(participant => {deleteParticipant(participant, mergeId, cascade);});
workingRows<Resource>('resource', findAll<Resource>('resources')).filter(resource => resource.status !== 'D' && resource.entityType === 'contact' && resource.entityId === contact.id).forEach(resource => {deleteResource(resource, mergeId, cascade);});
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('contactId', contact.id), ...store.getState().matters.getMappingFor('billToContactId', contact.id)]).filter(matterTemp => (matterTemp.clients?.includes(contact.id) && matterTemp.clients.some(other => other !== contact.id)) || matterTemp.billToContactId === contact.id).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
if (_matter.clients?.some(other => other !== contact.id)) _matter.clients = _matter.clients.filter(id => id !== contact.id);

if (_matter.billToContactId === contact.id) {
_matter.billToContactId = null;

}
_work.set('matter:' + _matter.id, _matter);
dispatch(Actions.MatterActions.saveMatterQueue(_matter), 'matter:' + _matter.id);
});
workingRows<Conflict>('conflict', [...store.getState().conflicts.getMappingFor('checkedContactIds', contact.id)]).filter(conflictTemp => conflictTemp.checkedContactIds?.includes(contact.id)).forEach(conflictTemp => {
const _conflict = ((_work.get('conflict:' + conflictTemp.id) ?? conflictTemp) as Conflict).clone();
if (_conflict.status === 'D') return;
_conflict.status = 'U';
_conflict.addedOn = newHRTimestamp();
_conflict.addedBy = getCurrentUserId();
_conflict.importSource = targetImportSource;
_conflict.importId = mergeId;
_conflict.checkedContactIds = _conflict.checkedContactIds?.filter(id => id !== contact.id) || [];

_work.set('conflict:' + _conflict.id, _conflict);
dispatch(Actions.ConflictActions.saveConflictQueue(_conflict), 'conflict:' + _conflict.id);
});
workingRows<Contact>('contact', [...store.getState().contacts.getMappingFor('companyId', contact.id)]).filter(contactTemp => contactTemp.companyId === contact.id).forEach(contactTemp => {
const _contact = ((_work.get('contact:' + contactTemp.id) ?? contactTemp) as Contact).clone();
if (_contact.status === 'D') return;
_contact.status = 'U';
_contact.addedOn = newHRTimestamp();
_contact.addedBy = getCurrentUserId();
_contact.importSource = targetImportSource;
_contact.importId = mergeId;
if (_contact.companyId === contact.id) {
_contact.companyId = null;

}
_work.set('contact:' + _contact.id, _contact);
dispatch(Actions.ContactActions.saveContactQueue(_contact), 'contact:' + _contact.id);
});
workingRows<Expense>('expense', [...store.getState().expenses.getMappingFor('payee', contact.id)]).filter(expenseTemp => expenseTemp.payee === contact.id).forEach(expenseTemp => {
const _expense = ((_work.get('expense:' + expenseTemp.id) ?? expenseTemp) as Expense).clone();
if (_expense.status === 'D') return;
_expense.status = 'U';
_expense.addedOn = newHRTimestamp();
_expense.addedBy = getCurrentUserId();
_expense.importSource = targetImportSource;
_expense.importId = mergeId;
if (_expense.payee === contact.id) {
_expense.payee = null;

}
_work.set('expense:' + _expense.id, _expense);
dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense), 'expense:' + _expense.id);
});
workingRows<Event>('event', [...store.getState().events.getMappingFor('participants', contact.id)]).filter(eventTemp => eventTemp.participants?.includes(contact.id)).forEach(eventTemp => {
const _event = ((_work.get('event:' + eventTemp.id) ?? eventTemp) as Event).clone();
if (_event.status === 'D') return;
_event.status = 'U';
_event.addedOn = newHRTimestamp();
_event.addedBy = getCurrentUserId();
_event.importSource = targetImportSource;
_event.importId = mergeId;
_event.participants = _event.participants?.filter(id => id !== contact.id) || [];

_work.set('event:' + _event.id, _event);
dispatch(Actions.EventActions.saveEventQueue(_event), 'event:' + _event.id);
});
workingRows<FlatFee>('flatFee', [...store.getState().flatFees.getMappingFor('relatedTo', contact.id)]).filter(flatFeeTemp => flatFeeTemp.relatedTo === contact.id).forEach(flatFeeTemp => {
const _flatFee = ((_work.get('flatFee:' + flatFeeTemp.id) ?? flatFeeTemp) as FlatFee).clone();
if (_flatFee.status === 'D') return;
_flatFee.status = 'U';
_flatFee.addedOn = newHRTimestamp();
_flatFee.addedBy = getCurrentUserId();
_flatFee.importSource = targetImportSource;
_flatFee.importId = mergeId;
if (_flatFee.relatedTo === contact.id) {
_flatFee.relatedTo = null;

}
_work.set('flatFee:' + _flatFee.id, _flatFee);
dispatch(Actions.FlatFeeActions.saveFlatFeeQueue(_flatFee), 'flatFee:' + _flatFee.id);
});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('clientIds', contact.id)]).filter(invoiceTemp => (invoiceTemp.clientIds?.includes(contact.id) && invoiceTemp.clientIds.some(other => other !== contact.id))).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
if (_invoice.clientIds?.some(other => other !== contact.id)) _invoice.clientIds = _invoice.clientIds.filter(id => id !== contact.id);

_work.set('invoice:' + _invoice.id, _invoice);
dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice), 'invoice:' + _invoice.id);
});
workingRows<Lead>('lead', [...store.getState().leads.getMappingFor('clients', contact.id), ...store.getState().leads.getMappingFor('payers', contact.id), ...store.getState().leads.getMappingFor('referrers', contact.id)]).filter(leadTemp => (leadTemp.clients?.includes(contact.id) && leadTemp.clients.some(other => other !== contact.id)) || leadTemp.payers?.includes(contact.id) || leadTemp.referrers?.includes(contact.id)).forEach(leadTemp => {
const _lead = ((_work.get('lead:' + leadTemp.id) ?? leadTemp) as Lead).clone();
if (_lead.status === 'D') return;
_lead.status = 'U';
_lead.addedOn = newHRTimestamp();
_lead.addedBy = getCurrentUserId();
_lead.importSource = targetImportSource;
_lead.importId = mergeId;
if (_lead.clients?.some(other => other !== contact.id)) _lead.clients = _lead.clients.filter(id => id !== contact.id);

_lead.payers = _lead.payers?.filter(id => id !== contact.id) || [];

_lead.referrers = _lead.referrers?.filter(id => id !== contact.id) || [];

_work.set('lead:' + _lead.id, _lead);
dispatch(Actions.LeadActions.saveLeadQueue(_lead), 'lead:' + _lead.id);
});
workingRows<Referral>('referral', [...store.getState().referrals.getMappingFor('referee', contact.id)]).filter(referralTemp => referralTemp.referee === contact.id).forEach(referralTemp => {
const _referral = ((_work.get('referral:' + referralTemp.id) ?? referralTemp) as Referral).clone();
if (_referral.status === 'D') return;
_referral.status = 'U';
_referral.addedOn = newHRTimestamp();
_referral.addedBy = getCurrentUserId();
_referral.importSource = targetImportSource;
_referral.importId = mergeId;
if (_referral.referee === contact.id) {
_referral.referee = null;

}
_work.set('referral:' + _referral.id, _referral);
dispatch(Actions.ReferralActions.saveReferralQueue(_referral), 'referral:' + _referral.id);
});
workingRows<Task>('task', [...store.getState().tasks.getMappingFor('contactId', contact.id)]).filter(taskTemp => taskTemp.relatedTo?.includes(contact.id)).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_task.relatedTo = _task.relatedTo?.filter(id => id !== contact.id) || [];

_work.set('task:' + _task.id, _task);
dispatch(Actions.TaskActions.saveTaskQueue(_task), 'task:' + _task.id);
});
workingRows<Transaction>('transaction', [...store.getState().transactions.getMappingFor('clientIds', contact.id)]).filter(transactionTemp => (transactionTemp.clientIds?.includes(contact.id) && transactionTemp.clientIds.some(other => other !== contact.id))).forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
if (_transaction.clientIds?.some(other => other !== contact.id)) _transaction.clientIds = _transaction.clientIds.filter(id => id !== contact.id);

_work.set('transaction:' + _transaction.id, _transaction);
dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction), 'transaction:' + _transaction.id);
});
workingRows<User>('user', [...store.getState().users.getMappingFor('contactId', contact.id)]).filter(userTemp => userTemp.contactId === contact.id).forEach(userTemp => {
const _user = ((_work.get('user:' + userTemp.id) ?? userTemp) as User).clone();
if (_user.status === 'D') return;
_user.status = 'U';
_user.addedOn = newHRTimestamp();
_user.addedBy = getCurrentUserId();
_user.importSource = targetImportSource;
_user.importId = mergeId;
if (_user.contactId === contact.id) {
_user.contactId = null;

}
_work.set('user:' + _user.id, _user);
dispatch(Actions.UserActions.saveUserQueue(_user), 'user:' + _user.id);
});workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => ['C','G'].includes(transactionTemp.sourceType || '') && transactionTemp.source === contact.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.source = null;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction), 'transaction:' + _transaction.id);
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => ['C','G'].includes(transactionTemp.targetType || '') && transactionTemp.target === contact.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.target = null;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction), 'transaction:' + _transaction.id);
});
const deleted = contact.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.ContactActions.removeContactQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceContact(contact: Contact, replaceWith: Contact, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = contact.id;}
const _work = workingSet();
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('contactId', contact.id), ...store.getState().matters.getMappingFor('billToContactId', contact.id)]).filter(matterTemp => matterTemp.clients?.includes(contact.id) || matterTemp.billToContactId === contact.id).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_matter.clients = _matter.clients?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

if (_matter.billToContactId === contact.id) {
_matter.billToContactId = replaceWith.id;

}
_work.set('matter:' + _matter.id, _matter);
store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
});
workingRows<Conflict>('conflict', [...store.getState().conflicts.getMappingFor('checkedContactIds', contact.id)]).filter(conflictTemp => conflictTemp.checkedContactIds?.includes(contact.id)).forEach(conflictTemp => {
const _conflict = ((_work.get('conflict:' + conflictTemp.id) ?? conflictTemp) as Conflict).clone();
if (_conflict.status === 'D') return;
_conflict.status = 'U';
_conflict.addedOn = newHRTimestamp();
_conflict.addedBy = getCurrentUserId();
_conflict.importSource = targetImportSource;
_conflict.importId = mergeId;
_conflict.checkedContactIds = _conflict.checkedContactIds?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_work.set('conflict:' + _conflict.id, _conflict);
store.dispatch(Actions.ConflictActions.saveConflictQueue(_conflict));
});
workingRows<Contact>('contact', [...store.getState().contacts.getMappingFor('companyId', contact.id)]).filter(contactTemp => contactTemp.companyId === contact.id).forEach(contactTemp => {
const _contact = ((_work.get('contact:' + contactTemp.id) ?? contactTemp) as Contact).clone();
if (_contact.status === 'D') return;
_contact.status = 'U';
_contact.addedOn = newHRTimestamp();
_contact.addedBy = getCurrentUserId();
_contact.importSource = targetImportSource;
_contact.importId = mergeId;
if (_contact.companyId === contact.id) {
_contact.companyId = replaceWith.id;

}
_work.set('contact:' + _contact.id, _contact);
store.dispatch(Actions.ContactActions.saveContactQueue(_contact));
});
workingRows<Expense>('expense', [...store.getState().expenses.getMappingFor('payee', contact.id)]).filter(expenseTemp => expenseTemp.payee === contact.id).forEach(expenseTemp => {
const _expense = ((_work.get('expense:' + expenseTemp.id) ?? expenseTemp) as Expense).clone();
if (_expense.status === 'D') return;
_expense.status = 'U';
_expense.addedOn = newHRTimestamp();
_expense.addedBy = getCurrentUserId();
_expense.importSource = targetImportSource;
_expense.importId = mergeId;
if (_expense.payee === contact.id) {
_expense.payee = replaceWith.id;

}
_work.set('expense:' + _expense.id, _expense);
store.dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense));
});
workingRows<Event>('event', [...store.getState().events.getMappingFor('participants', contact.id)]).filter(eventTemp => eventTemp.participants?.includes(contact.id)).forEach(eventTemp => {
const _event = ((_work.get('event:' + eventTemp.id) ?? eventTemp) as Event).clone();
if (_event.status === 'D') return;
_event.status = 'U';
_event.addedOn = newHRTimestamp();
_event.addedBy = getCurrentUserId();
_event.importSource = targetImportSource;
_event.importId = mergeId;
_event.participants = _event.participants?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_work.set('event:' + _event.id, _event);
store.dispatch(Actions.EventActions.saveEventQueue(_event));
});
workingRows<FlatFee>('flatFee', [...store.getState().flatFees.getMappingFor('relatedTo', contact.id)]).filter(flatFeeTemp => flatFeeTemp.relatedTo === contact.id).forEach(flatFeeTemp => {
const _flatFee = ((_work.get('flatFee:' + flatFeeTemp.id) ?? flatFeeTemp) as FlatFee).clone();
if (_flatFee.status === 'D') return;
_flatFee.status = 'U';
_flatFee.addedOn = newHRTimestamp();
_flatFee.addedBy = getCurrentUserId();
_flatFee.importSource = targetImportSource;
_flatFee.importId = mergeId;
if (_flatFee.relatedTo === contact.id) {
_flatFee.relatedTo = replaceWith.id;

}
_work.set('flatFee:' + _flatFee.id, _flatFee);
store.dispatch(Actions.FlatFeeActions.saveFlatFeeQueue(_flatFee));
});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('clientIds', contact.id)]).filter(invoiceTemp => invoiceTemp.clientIds?.includes(contact.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.clientIds = _invoice.clientIds?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
});
workingRows<Lead>('lead', [...store.getState().leads.getMappingFor('clients', contact.id), ...store.getState().leads.getMappingFor('payers', contact.id), ...store.getState().leads.getMappingFor('referrers', contact.id)]).filter(leadTemp => leadTemp.clients?.includes(contact.id) || leadTemp.payers?.includes(contact.id) || leadTemp.referrers?.includes(contact.id)).forEach(leadTemp => {
const _lead = ((_work.get('lead:' + leadTemp.id) ?? leadTemp) as Lead).clone();
if (_lead.status === 'D') return;
_lead.status = 'U';
_lead.addedOn = newHRTimestamp();
_lead.addedBy = getCurrentUserId();
_lead.importSource = targetImportSource;
_lead.importId = mergeId;
_lead.clients = _lead.clients?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_lead.payers = _lead.payers?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_lead.referrers = _lead.referrers?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_work.set('lead:' + _lead.id, _lead);
store.dispatch(Actions.LeadActions.saveLeadQueue(_lead));
});
workingRows<Participant>('participant', [...store.getState().participants.getMappingFor('contactId', contact.id)]).filter(participantTemp => participantTemp.contactId === contact.id).forEach(participantTemp => {
const _participant = ((_work.get('participant:' + participantTemp.id) ?? participantTemp) as Participant).clone();
if (_participant.status === 'D') return;
_participant.status = 'U';
_participant.addedOn = newHRTimestamp();
_participant.addedBy = getCurrentUserId();
_participant.importSource = targetImportSource;
_participant.importId = mergeId;
if (_participant.contactId === contact.id) {
_participant.contactId = replaceWith.id;

}
_work.set('participant:' + _participant.id, _participant);
store.dispatch(Actions.ParticipantActions.saveParticipantQueue(_participant));
});
workingRows<Referral>('referral', [...store.getState().referrals.getMappingFor('referee', contact.id)]).filter(referralTemp => referralTemp.referee === contact.id).forEach(referralTemp => {
const _referral = ((_work.get('referral:' + referralTemp.id) ?? referralTemp) as Referral).clone();
if (_referral.status === 'D') return;
_referral.status = 'U';
_referral.addedOn = newHRTimestamp();
_referral.addedBy = getCurrentUserId();
_referral.importSource = targetImportSource;
_referral.importId = mergeId;
if (_referral.referee === contact.id) {
_referral.referee = replaceWith.id;

}
_work.set('referral:' + _referral.id, _referral);
store.dispatch(Actions.ReferralActions.saveReferralQueue(_referral));
});
workingRows<Task>('task', [...store.getState().tasks.getMappingFor('contactId', contact.id)]).filter(taskTemp => taskTemp.relatedTo?.includes(contact.id)).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_task.relatedTo = _task.relatedTo?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});
workingRows<Transaction>('transaction', [...store.getState().transactions.getMappingFor('clientIds', contact.id)]).filter(transactionTemp => transactionTemp.clientIds?.includes(contact.id)).forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_transaction.clientIds = _transaction.clientIds?.map(id => (id === contact.id) ? replaceWith.id : id) || [];

_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});
workingRows<User>('user', [...store.getState().users.getMappingFor('contactId', contact.id)]).filter(userTemp => userTemp.contactId === contact.id).forEach(userTemp => {
const _user = ((_work.get('user:' + userTemp.id) ?? userTemp) as User).clone();
if (_user.status === 'D') return;
_user.status = 'U';
_user.addedOn = newHRTimestamp();
_user.addedBy = getCurrentUserId();
_user.importSource = targetImportSource;
_user.importId = mergeId;
if (_user.contactId === contact.id) {
_user.contactId = replaceWith.id;

}
_work.set('user:' + _user.id, _user);
store.dispatch(Actions.UserActions.saveUserQueue(_user));
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => ['C','G'].includes(transactionTemp.sourceType || '') && transactionTemp.source === contact.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.source = replaceWith.id;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});
workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionTemp => ['C','G'].includes(transactionTemp.targetType || '') && transactionTemp.target === contact.id && transactionTemp.status !== 'D').forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.target = replaceWith.id;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});


workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'contact' && comment.entityId === contact.id).forEach(commentTemp => {
const _comment = ((_work.get('comment:' + commentTemp.id) ?? commentTemp) as Comment).clone();
if (_comment.status === 'D') return;
_comment.entityId = replaceWith.id;
_comment.status = 'U';
_comment.addedOn = newHRTimestamp();
_comment.addedBy = getCurrentUserId();
_comment.importSource = targetImportSource;
_comment.importId = mergeId;
_work.set('comment:' + _comment.id, _comment);
store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'contact' && document.entityId === contact.id).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.entityId = replaceWith.id;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'contact' && event.entityId === contact.id).forEach(eventTemp => {
const _event = ((_work.get('event:' + eventTemp.id) ?? eventTemp) as Event).clone();
if (_event.status === 'D') return;
_event.entityId = replaceWith.id;
_event.status = 'U';
_event.addedOn = newHRTimestamp();
_event.addedBy = getCurrentUserId();
_event.importSource = targetImportSource;
_event.importId = mergeId;
_work.set('event:' + _event.id, _event);
store.dispatch(Actions.EventActions.saveEventQueue(_event));
});
workingRows<Identification>('identification', findAll<Identification>('identifications')).filter(identification => identification.status !== 'D' && identification.entityType === 'contact' && identification.entityId === contact.id).forEach(identificationTemp => {
const _identification = ((_work.get('identification:' + identificationTemp.id) ?? identificationTemp) as Identification).clone();
if (_identification.status === 'D') return;
_identification.entityId = replaceWith.id;
_identification.status = 'U';
_identification.addedOn = newHRTimestamp();
_identification.addedBy = getCurrentUserId();
_identification.importSource = targetImportSource;
_identification.importId = mergeId;
_work.set('identification:' + _identification.id, _identification);
store.dispatch(Actions.IdentificationActions.saveIdentificationQueue(_identification));
});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'contact' && participant.entityId === contact.id).forEach(participantTemp => {
const _participant = ((_work.get('participant:' + participantTemp.id) ?? participantTemp) as Participant).clone();
if (_participant.status === 'D') return;
_participant.entityId = replaceWith.id;
_participant.status = 'U';
_participant.addedOn = newHRTimestamp();
_participant.addedBy = getCurrentUserId();
_participant.importSource = targetImportSource;
_participant.importId = mergeId;
_work.set('participant:' + _participant.id, _participant);
store.dispatch(Actions.ParticipantActions.saveParticipantQueue(_participant));
});
return contact;
}
